import { AlertTriangle, Star, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Badge } from "@v2/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@v2/components/ui/dialog";
import { fixEaText, resolveEaPlayerName, type EaLinkRosterRow } from "../../lib/ea-name";
import { currentIntlLocale } from "../../lib/i18n";
import { isMatchUnplayed } from "../../lib/ea-match-outcome";
import { ourClubIdOf } from "../../lib/ea-game";
import type { EaMatch } from "./types";

function fmtMmSs(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "-";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}'${String(s).padStart(2, "0")}"`;
}

function pct(num: number, den: number) {
  if (!den) return "-";
  return `${Math.round((num / den) * 100)}%`;
}

export function MatchDetailDialog({
  match,
  eaClubId,
  rosterLookup,
  open,
  onOpenChange,
}: {
  match: EaMatch | null;
  eaClubId: string;
  rosterLookup?: Map<string, EaLinkRosterRow>;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation("mondofc");
  if (!match) return null;
  const d = match.data || {};
  const ourId = ourClubIdOf(d, eaClubId);
  const clubs = d.clubs || {};
  const ourClub: any = clubs[ourId] || {};
  const oppId: string | undefined = Object.keys(clubs).find((id) => id !== ourId);
  const oppClub: any = oppId ? clubs[oppId] : null;
  const playersByClub = d.players || {};
  const ourPlayers = Object.values(playersByClub[ourId] || {}) as any[];
  const oppPlayers = oppId ? (Object.values(playersByClub[oppId] || {}) as any[]) : [];

  // KPI: EA a volte popola club.* a 0 anche quando i player hanno valori reali.
  // Fallback: aggreghiamo dai player. Stessa cosa per i goal (vedi bug 2-1 vs 1-1):
  // se la somma player-level è diversa dal team-level, ci fidiamo della somma player.
  const sumPlayers = (arr: any[], key: string): number =>
    arr.reduce((acc, p) => acc + Number(p?.[key] || 0), 0);

  const ourPlayerGoals = sumPlayers(ourPlayers, "goals");
  const oppPlayerGoals = sumPlayers(oppPlayers, "goals");
  const ourClubGoals = Number(ourClub?.goals ?? 0);
  const oppClubGoals = Number(oppClub?.goals ?? 0);
  // Se i player sommano valori e il team-level è 0, oppure se la somma player
  // è maggiore (autogoal contati al team avversario), preferiamo la somma player.
  const ourGoals = ourPlayerGoals > ourClubGoals ? ourPlayerGoals : ourClubGoals;
  const oppGoals = oppPlayerGoals > oppClubGoals ? oppPlayerGoals : oppClubGoals;
  const result = ourGoals > oppGoals ? "win" : ourGoals < oppGoals ? "lose" : "draw";

  // Stessa logica per shots / pass / tackles / saves: fallback su somma player.
  const ourShots = Number(ourClub?.shots ?? 0) || sumPlayers(ourPlayers, "shots");
  const oppShots = Number(oppClub?.shots ?? 0) || sumPlayers(oppPlayers, "shots");
  const ourPassMade = Number(ourClub?.passesmade ?? 0) || sumPlayers(ourPlayers, "passesmade");
  const ourPassAtt = Number(ourClub?.passattempts ?? 0) || sumPlayers(ourPlayers, "passattempts");
  const oppPassAtt = Number(oppClub?.passattempts ?? 0) || sumPlayers(oppPlayers, "passattempts");

  const matchType = String(d.match_type || "");
  const typeLabel = matchType
    ? (t(`matchTypes.${matchType}`, { defaultValue: matchType }) as string)
    : t("matchTypes.match");

  // Su mobile la riga descrizione è stretta: data lunga (es. "18 maggio 2026
  // alle 19:30") veniva troncata e diventava illeggibile. Tengo formato lungo
  // su desktop e formato corto su mobile.
  const dateLabelLong = match.played_at
    ? new Date(match.played_at).toLocaleString(currentIntlLocale(), {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : t("matchDialog.dateNa");
  const dateLabelShort = match.played_at
    ? new Date(match.played_at).toLocaleDateString(currentIntlLocale(), {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : t("matchDialog.dateNa");

  const sortPlayers = (arr: any[]) =>
    [...arr].sort((a, b) => {
      const am = Number(a.mom) === 1 ? 1 : 0;
      const bm = Number(b.mom) === 1 ? 1 : 0;
      if (am !== bm) return bm - am;
      return Number(b.rating || 0) - Number(a.rating || 0);
    });

  const ourSorted = sortPlayers(ourPlayers);
  const oppSorted = sortPlayers(oppPlayers);

  const ourAvg =
    ourPlayers.length > 0
      ? ourPlayers.reduce((s, p) => s + Number(p.rating || 0), 0) / ourPlayers.length
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] max-w-[calc(100vw-1rem)] sm:max-w-4xl",
          "max-h-[90dvh] overflow-y-auto overflow-x-hidden",
          "p-4 sm:p-6",
        )}
      >
        {/* `text-left` override del default `text-center` di DialogHeader: su
            mobile il centered header faceva sembrare il titolo schiacciato
            sotto la X di chiusura. */}
        <DialogHeader className="text-left space-y-1">
          <DialogDescription className="uppercase tracking-wider text-xs sm:text-xs font-semibold text-muted-foreground truncate pr-8">
            <span className="sm:hidden">{typeLabel} · {dateLabelShort}</span>
            <span className="hidden sm:inline">{typeLabel} · {dateLabelLong}</span>
          </DialogDescription>
          {/* Mobile: club sopra / sotto col chip a sx, riserva spazio (`pr-8`)
              per la X di chiusura. Desktop: tutto inline come prima. */}
          <DialogTitle className="text-base sm:text-xl flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 sm:flex-wrap pr-8 sm:pr-0">
            <span className="truncate">
              {fixEaText(ourClub?.details?.name) || t("matchDialog.ourTeam")}
            </span>
            <span
              className={cn(
                "score-chip self-start sm:self-auto",
                result === "win" && "win",
                result === "lose" && "lose",
                result === "draw" && "draw",
              )}
            >
              {ourGoals}-{oppGoals}
            </span>
            <span className="truncate">
              {fixEaText(oppClub?.details?.name) || (oppId ? t("matchDialog.clubFallback", { id: oppId }) : t("matchDialog.opponent"))}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Alert "partita non disputata": voti dei nostri giocatori tutti
            identici (= valori di default EA, tipicamente 6.0). E' il segnale
            più affidabile per distinguere un forfait/no-contest da una
            partita effettivamente giocata, indipendentemente dal punteggio. */}
        {isMatchUnplayed(d, ourId) && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <div className="leading-snug">
              <p className="font-semibold">{t("matchDialog.unplayedTitle")}</p>
              <p className="text-xs mt-0.5">{t("matchDialog.unplayedBody")}</p>
            </div>
          </div>
        )}

        <section className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <KpiCell label={t("matchDialog.kpiAvg")} value={ourAvg > 0 ? ourAvg.toFixed(2) : "-"} />
          <KpiCell
            label={t("matchDialog.kpiShots")}
            value={ourShots || oppShots ? `${ourShots} / ${oppShots}` : "-"}
            hint={t("matchDialog.kpiShotsHint")}
          />
          <KpiCell
            label={t("matchDialog.kpiPossession")}
            value={
              ourPassAtt || oppPassAtt
                ? `${pct(ourPassAtt, ourPassAtt + oppPassAtt)}`
                : "-"
            }
            hint={t("matchDialog.kpiPossessionHint")}
          />
          <KpiCell
            label={t("matchDialog.kpiPasses")}
            value={pct(ourPassMade, ourPassAtt)}
            hint={`${ourPassMade}/${ourPassAtt}`}
          />
        </section>

        <section className="space-y-2 mt-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">{t("matchDialog.ourTeam")}</h3>
            <span className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
              {/* Solo in verticale su mobile: in orizzontale (>= sm) la
                  tabella mostra tiri, passaggi, contrasti, parate, cartellini
                  e minuti. */}
              <span className="sm:hidden" title={t("matchDialog.rotateHint")}>
                <RotateToLandscapeIcon className="h-4 w-4" />
                <span className="sr-only">{t("matchDialog.rotateHint")}</span>
              </span>
              {t("matchDialog.playersCount", { count: ourPlayers.length })}
            </span>
          </div>
          <PlayerRatingsTable players={ourSorted} rosterLookup={rosterLookup} />
        </section>

        {oppPlayers.length > 0 && (
          <section className="space-y-2 mt-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm text-muted-foreground">
                {fixEaText(oppClub?.details?.name) || t("matchDialog.opponent")}
              </h3>
              <span className="text-xs text-muted-foreground tabular-nums">{t("matchDialog.playersCount", { count: oppPlayers.length })}</span>
            </div>
            <PlayerRatingsTable players={oppSorted} muted />
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KpiCell({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    // `min-w-0` + `overflow-hidden` evitano che un valore come "29%" più la
    // hint sotto facciano sforare la cella nella seconda colonna del grid 2x2.
    // Valore `text-base` su mobile per stare anche a 360px di viewport.
    <div className="rounded-2xl bg-raised px-2.5 sm:px-3 py-2 min-w-0 overflow-hidden">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold truncate">{label}</p>
      <p className="text-base sm:text-lg font-bold tabular-nums leading-tight truncate">{value}</p>
      {hint && <p className="text-xs text-muted-foreground tabular-nums truncate">{hint}</p>}
    </div>
  );
}

// Abbrevia la position EA per non far sforare la colonna su mobile.
// "FORWARD" → "FW", "MIDFIELDER" → "MID", "DEFENDER" → "DEF", "GOALKEEPER" → "GK".
// Posizioni gia' corte (ST, CAM, RB, ecc.) passano invariate.
function shortPos(raw: unknown): string {
  if (raw === null || raw === undefined) return "-";
  const up = String(raw).trim().toUpperCase();
  if (!up) return "-";
  switch (up) {
    case "GOALKEEPER":
      return "GK";
    case "DEFENDER":
      return "DEF";
    case "MIDFIELDER":
      return "MID";
    case "FORWARD":
      return "FW";
    case "ANYONE":
      return "-";
    default:
      return up.length > 4 ? up.slice(0, 4) : up;
  }
}

// Telefono in verticale con freccia di rotazione verso l'orizzontale.
function RotateToLandscapeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="7" width="9" height="15" rx="1.5" />
      <path d="M6.5 19h2" />
      <path d="M14 3a7 7 0 0 1 7 7" />
      <path d="m18.5 9.5 2.5.5.5-2.5" />
    </svg>
  );
}

function PlayerRatingsTable({ players, muted = false, rosterLookup }: { players: any[]; muted?: boolean; rosterLookup?: Map<string, EaLinkRosterRow> }) {
  const { t } = useTranslation("mondofc");
  if (players.length === 0) {
    return <p className="text-xs text-muted-foreground italic">{t("matchDialog.noPlayers")}</p>;
  }
  return (
    // Su mobile niente scroll orizzontale: tengo solo colonne essenziali
    // (player, pos, voto, G, A). Le altre (`hidden sm:table-cell`) tornano
    // visibili da `sm` in su, dove la tabella ha spazio per il min-w-[640px].
    <div className="rounded-2xl bg-raised -mx-1 sm:mx-0 sm:overflow-x-auto">
      <table className="w-full sm:min-w-[640px] text-sm">
        <thead className="text-muted-foreground">
          <tr className="border-b">
            <th className="text-left font-mono uppercase text-xs tracking-wider py-1.5 pl-2 sm:pl-3 pr-1 sm:pr-2 whitespace-nowrap">{t("matchDialog.th.player")}</th>
            <th className="text-center font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.pos")}</th>
            <th className="text-right font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.rating")}</th>
            <th className="text-right font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.goals")}</th>
            <th className="text-right font-mono uppercase text-xs tracking-wider px-1 pr-2 sm:pr-1">{t("matchDialog.th.assists")}</th>
            <th className="hidden sm:table-cell text-right font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.shots")}</th>
            <th className="hidden sm:table-cell text-right font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.passes")}</th>
            <th className="hidden sm:table-cell text-right font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.tackles")}</th>
            <th className="hidden sm:table-cell text-right font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.saves")}</th>
            <th className="hidden sm:table-cell text-right font-mono uppercase text-xs tracking-wider px-1">{t("matchDialog.th.card")}</th>
            <th className="hidden sm:table-cell text-right font-mono uppercase text-xs tracking-wider px-1 pr-3">{t("matchDialog.th.min")}</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p, idx) => {
            const rating = Number(p.rating || 0);
            const isMom = Number(p.mom) === 1;
            const goals = Number(p.goals || 0);
            const assists = Number(p.assists || 0);
            const yellow = Number(p.yellowcards || 0);
            const red = Number(p.redcards || 0);
            const shots = Number(p.shots || 0);
            const passesMade = Number(p.passesmade || 0);
            const passAtt = Number(p.passattempts || 0);
            const tacklesMade = Number(p.tacklesmade || 0);
            const tackleAtt = Number(p.tackleattempts || 0);
            const saves = Number(p.saves || 0);
            const seconds = Number(p.secondsPlayed || p.gameTime || 0);
            const resolved = rosterLookup
              ? resolveEaPlayerName(p, rosterLookup)
              : { displayName: p.proName || p.playername || p.name || "-", linked: false };
            const name = resolved.displayName;
            const showOriginal = resolved.linked && p.playername && p.playername !== name;
            return (
              <tr key={idx} className={cn("border-t", isMom && !muted && "bg-amber-50/60", muted && "text-muted-foreground")}>
                <td className="py-1.5 pl-2 sm:pl-3 pr-1 sm:pr-2 align-middle">
                  {/* Wrapper con max-width esplicito + overflow-hidden: i `<td>`
                      in tabella non rispettano `max-w-*` da soli (ignorano
                      `max-width` salvo `table-layout: fixed`), quindi serve un
                      contenitore block dentro la cella per troncare. */}
                  <div className="flex items-center gap-1.5 min-w-0 max-w-[150px] sm:max-w-[220px] overflow-hidden">
                    {isMom && <Trophy className="h-3 w-3 text-amber-500 shrink-0" aria-label={t("matchDialog.momLabel")} />}
                    <div className="min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:gap-1.5 flex-1">
                      <span className="font-medium truncate leading-tight">{name}</span>
                      {showOriginal && (
                        <span className="text-xs text-muted-foreground font-mono truncate leading-tight">
                          {p.playername}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="text-center px-1 text-xs sm:text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap align-middle">
                  {shortPos(p.position || p.pos)}
                </td>
                <td className="text-right px-1 font-tabular align-middle">
                  <span className={cn("inline-flex items-center gap-1", rating >= 8 && "text-emerald-600 font-semibold", rating < 6 && rating > 0 && "text-rose-600")}>
                    {rating > 0 ? rating.toFixed(1) : "-"}
                    {rating >= 9 && <Star className="h-3 w-3 fill-current" />}
                  </span>
                </td>
                <td className="text-right px-1 font-tabular align-middle">
                  {goals > 0 ? <span className="text-emerald-600 font-semibold">{goals}</span> : <span className="text-muted-foreground">·</span>}
                </td>
                <td className="text-right px-1 pr-2 sm:pr-1 font-tabular align-middle">
                  {assists > 0 ? <span className="text-amber-600 font-semibold">{assists}</span> : <span className="text-muted-foreground">·</span>}
                </td>
                <td className="hidden sm:table-cell text-right px-1 font-tabular text-xs align-middle">
                  {shots > 0 ? shots : "-"}
                </td>
                <td className="hidden sm:table-cell text-right px-1 font-tabular text-xs align-middle">
                  {passAtt > 0 ? `${passesMade}/${passAtt}` : "-"}
                </td>
                <td className="hidden sm:table-cell text-right px-1 font-tabular text-xs align-middle">
                  {tackleAtt > 0 ? `${tacklesMade}/${tackleAtt}` : "-"}
                </td>
                <td className="hidden sm:table-cell text-right px-1 font-tabular text-xs align-middle">
                  {saves > 0 ? saves : "-"}
                </td>
                <td className="hidden sm:table-cell text-right px-1 font-tabular text-xs align-middle">
                  {yellow > 0 || red > 0 ? (
                    <span className="inline-flex items-center gap-0.5">
                      {yellow > 0 && (
                        <Badge variant="secondary" className="px-1 py-0 h-4 text-[11px] bg-amber-200 text-amber-900">
                          {yellow > 1 ? yellow : ""}Y
                        </Badge>
                      )}
                      {red > 0 && (
                        <Badge variant="secondary" className="px-1 py-0 h-4 text-[11px] bg-rose-200 text-rose-900">
                          R
                        </Badge>
                      )}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">·</span>
                  )}
                </td>
                <td className="hidden sm:table-cell text-right px-1 pr-3 font-tabular text-xs text-muted-foreground align-middle">
                  {fmtMmSs(seconds)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
